@let s = state();
@let f = folders();
<div #specificContainer class="iu-specific-window docs" [attr.data-cy-specific-window-name]="name" (keydown)="onWindowKey($event)">
  <h1>{{ name || ('documents.title' | translate) }}</h1>

  <!-- toolbar: navigation, upload, search, view -->
  <div class="docs-toolbar" role="toolbar" [attr.aria-label]="'documents.title' | translate">
    <button
      type="button"
      class="p-button p-button-secondary docs-tool-btn docs-tool-btn--icon"
      [attr.aria-expanded]="navOpen()"
      aria-controls="docs-nav"
      (click)="navOpen.set(!navOpen())"
      [attr.aria-label]="'documents.nav.toggle' | translate"
      [title]="'documents.nav.toggle' | translate">
      <i class="fa-solid fa-bars" aria-hidden="true"></i>
    </button>
    @if (canWrite() && !inBin()) {
      <button type="button" class="p-button p-button-primary docs-tool-btn" (click)="pickFiles()">
        <i class="fa-solid fa-cloud-arrow-up" aria-hidden="true"></i>{{ 'documents.actions.upload' | translate }}
      </button>
    }
    <label class="docs-search">
      <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
      <span class="visually-hidden">{{ 'documents.search.label' | translate }}</span>
      <input
        #searchInput
        type="search"
        class="p-inputtext doc-input"
        [placeholder]="(s.nameOnly ? 'documents.search.placeholderName' : 'documents.search.placeholder') | translate"
        [value]="s.q"
        (input)="onSearchInput(searchInput.value)"
        (keydown.escape)="searchInput.value = ''; onSearchInput('')" />
      <kbd class="docs-kbd" aria-hidden="true">/</kbd>
    </label>
    <label class="docs-check" [title]="'documents.search.nameOnlyHelp' | translate">
      <input type="checkbox" [checked]="s.nameOnly" (change)="setNameOnly($any($event.target).checked)" />
      {{ 'documents.search.nameOnly' | translate }}
    </label>
    @if (f?.canManageTags) {
      <button type="button" class="p-button p-button-secondary docs-tool-btn docs-tool-btn--icon" (click)="tagManagerOpen.set(true)" [attr.aria-label]="'documents.tagManager.title' | translate" [title]="'documents.tagManager.title' | translate">
        <i class="fa-solid fa-tags" aria-hidden="true"></i>
      </button>
    }
    <span class="docs-spacer"></span>
    @if (f) {
      <span class="docs-storage" [title]="'documents.storageHelp' | translate">
        <i class="fa-solid fa-database" aria-hidden="true"></i>
        {{ 'documents.storage' | translate: { count: fmt().number(f.total), size: size(f.totalSize) } }}
      </span>
    }
    <div class="docs-views" role="radiogroup" [attr.aria-label]="'documents.view.label' | translate">
      <button type="button" role="radio" class="p-button docs-tool-btn docs-tool-btn--icon" [class.p-button-primary]="s.view === 'list'" [class.p-button-secondary]="s.view !== 'list'" [attr.aria-checked]="s.view === 'list'" (click)="setView('list')" [attr.aria-label]="'documents.view.list' | translate" [title]="'documents.view.list' | translate">
        <i class="fa-solid fa-list" aria-hidden="true"></i>
      </button>
      <button type="button" role="radio" class="p-button docs-tool-btn docs-tool-btn--icon" [class.p-button-primary]="s.view === 'tiles'" [class.p-button-secondary]="s.view !== 'tiles'" [attr.aria-checked]="s.view === 'tiles'" (click)="setView('tiles')" [attr.aria-label]="'documents.view.tiles' | translate" [title]="'documents.view.tiles' | translate">
        <i class="fa-solid fa-table-cells-large" aria-hidden="true"></i>
      </button>
    </div>
    <button type="button" class="p-button p-button-secondary docs-tool-btn docs-tool-btn--icon" (click)="reload()" [attr.aria-label]="'documents.actions.refresh' | translate" [title]="'documents.actions.refresh' | translate">
      <i class="fa-solid fa-rotate-right" aria-hidden="true"></i>
    </button>
  </div>

  <div class="docs-layout" [class.docs-layout--nav]="navOpen()" [class.docs-layout--detail]="detailOpen()">
    <!-- navigation: virtual folders -->
    @if (navOpen()) {
      <nav id="docs-nav" class="docs-nav" [attr.aria-label]="'documents.nav.label' | translate">
        <ul class="docs-folders">
          @for (k of folderKinds; track k) {
            <li>
              <button type="button" class="docs-folder" [class.docs-folder--on]="isFolderActive(k)" [attr.aria-current]="isFolderActive(k) ? 'page' : null" (click)="openFolder(k)">
                <i [class]="folderIcon(k)" aria-hidden="true"></i>
                <span class="docs-folder-label">{{ 'documents.folders.' + k | translate }}</span>
                @if (folderCount(k) !== null) {
                  <span class="doc-count">{{ fmt().number(folderCount(k)) }}</span>
                }
              </button>
            </li>
          }
        </ul>
        @if (f && f.trashRetentionDays > 0 && s.folder === 'trash') {
          <p class="docs-nav-note">{{ 'documents.folders.trashHelp' | translate: { days: f.trashRetentionDays } }}</p>
        }

        <h2 class="docs-nav-title">{{ 'documents.views.title' | translate }}</h2>
        <ul class="docs-folders">
          @for (v of views(); track v.id) {
            <li class="docs-view">
              <button type="button" class="docs-folder" [class.docs-folder--on]="currentQuery() === v.query" (click)="applyView(v)" [title]="v.name">
                <i class="fa-solid fa-bookmark" aria-hidden="true"></i>
                <span class="docs-folder-label">{{ v.name }}</span>
              </button>
              <button type="button" class="doc-icon-btn docs-view-x" (click)="deleteView(v)" [attr.aria-label]="'documents.views.delete' | translate: { name: v.name }" [title]="'documents.views.delete' | translate: { name: v.name }">
                <i class="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </li>
          } @empty {
            <li class="doc-muted docs-nav-note">{{ 'documents.views.none' | translate }}</li>
          }
        </ul>

        @if (f?.types?.length) {
          <h2 class="docs-nav-title">{{ 'documents.nav.types' | translate }}</h2>
          <ul class="docs-folders">
            @for (t of f!.types; track t.value) {
              <li>
                <button type="button" class="docs-folder" [class.docs-folder--on]="s.folder === 'all' && s.type === t.value && !s.tableId" (click)="openType(t.value)" [title]="size(t.size)">
                  <i class="fa-solid fa-folder" aria-hidden="true"></i>
                  <span class="docs-folder-label">{{ typeLabel(t.value) }}</span>
                  <span class="doc-count">{{ fmt().number(t.count) }}</span>
                </button>
              </li>
            }
          </ul>
        }

        @if (f?.tables?.length) {
          <h2 class="docs-nav-title">{{ 'documents.nav.records' | translate }}</h2>
          @if (f!.tables.length > 8) {
            <label class="docs-nav-filter">
              <span class="visually-hidden">{{ 'documents.nav.filterTables' | translate }}</span>
              <input type="search" class="p-inputtext doc-input" [placeholder]="'documents.nav.filterTables' | translate" [value]="tableFilter()" (input)="tableFilter.set($any($event.target).value)" />
            </label>
          }
          <ul class="docs-folders docs-folders--tables">
            @for (t of tableFolders(); track t.value) {
              <li>
                <button type="button" class="docs-folder" [class.docs-folder--on]="s.folder === 'all' && s.tableId === num(t.value) && !s.type" (click)="openTable(num(t.value))" [title]="t.tableName + ' - ' + size(t.size)">
                  <i class="fa-solid fa-table-list" aria-hidden="true"></i>
                  <span class="docs-folder-label">{{ t.label }}</span>
                  <span class="doc-count">{{ fmt().number(t.count) }}</span>
                </button>
              </li>
            } @empty {
              <li class="doc-muted docs-nav-note">{{ 'documents.nav.noTable' | translate }}</li>
            }
          </ul>
        }
        @if (foldersState() === 'error') {
          <p class="doc-state doc-state--error"><i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ 'documents.state.foldersError' | translate }}</p>
        }
      </nav>
    }

    <!-- list -->
    <section
      class="docs-main"
      [attr.aria-label]="'documents.list.label' | translate"
      (dragenter)="onDragEnter($event)"
      (dragover)="onDragOver($event)"
      (dragleave)="onDragLeave($event)"
      (drop)="onDrop($event)">
      <div class="docs-main-head">
        <h2 class="docs-crumbs">
          @if (s.type) {
            {{ typeLabel(s.type) }}
          } @else if (s.tableId) {
            <button type="button" class="doc-link-btn" [disabled]="!s.recordId" (click)="patch({ recordId: null })">{{ tableLabel() }}</button>
            @if (s.recordId) {
              <i class="fa-solid fa-chevron-right docs-crumb-sep" aria-hidden="true"></i>{{ recordLabel() }}
            }
          } @else {
            {{ 'documents.folders.' + s.folder | translate }}
          }
          @if (pageInfo(); as pi) {
            <span class="doc-count">{{ fmt().number(pi.total) }}</span>
          }
        </h2>
        <span class="docs-spacer"></span>
        @if (savingView()) {
          <form class="docs-save-view" (submit)="$event.preventDefault(); saveView(viewName.value)">
            <input #viewName class="p-inputtext doc-input" maxlength="80" [placeholder]="'documents.views.name' | translate" (keydown.escape)="savingView.set(false)" />
            <button type="submit" class="p-button p-button-primary docs-tool-btn">{{ 'documents.actions.save' | translate }}</button>
            <button type="button" class="p-button p-button-secondary docs-tool-btn" (click)="savingView.set(false)">{{ 'documents.actions.cancel' | translate }}</button>
          </form>
        } @else {
          <button type="button" class="p-button p-button-secondary docs-tool-btn" (click)="startSaveView()" [title]="'documents.views.saveHelp' | translate">
            <i class="fa-regular fa-bookmark" aria-hidden="true"></i>{{ 'documents.views.save' | translate }}
          </button>
        }
        <button type="button" class="p-button p-button-secondary docs-tool-btn" [attr.aria-expanded]="filtersOpen()" aria-controls="docs-filters" (click)="filtersOpen.set(!filtersOpen())">
          <i class="fa-solid fa-filter" aria-hidden="true"></i>{{ 'documents.filters.label' | translate }}
          @if (activeKeys().length) {
            <span class="doc-count">{{ activeKeys().length }}</span>
          }
        </button>
        <label class="docs-sort">
          <span class="visually-hidden">{{ 'documents.sort.label' | translate }}</span>
          @let currentSort = s.sort ?? (s.q.trim() && !s.nameOnly ? 'relevance' : 'date');
          <select class="p-inputtext doc-input" (change)="patch({ sort: $any($event.target).value, asc: null }, false)">
            @if (s.q.trim() && !s.nameOnly) {
              <option value="relevance" [selected]="currentSort === 'relevance'">{{ 'documents.sort.relevance' | translate }}</option>
            }
            @for (o of sorts; track o) {
              <option [value]="o" [selected]="currentSort === o">{{ 'documents.sort.' + o | translate }}</option>
            }
          </select>
        </label>
      </div>

      @if (filtersOpen()) {
        <div id="docs-filters" class="docs-filters">
          <label class="docs-field">
            <span>{{ 'documents.filters.ext' | translate }}</span>
            <select class="p-inputtext doc-input" (change)="patch({ ext: $any($event.target).value || null })">
              <option value="" [selected]="!s.ext">{{ 'documents.filters.any' | translate }}</option>
              @for (e of extensions; track e) {
                <option [value]="e" [selected]="s.ext === e">.{{ e }}</option>
              }
              @if (s.ext && !extensions.includes(s.ext)) {
                <option [value]="s.ext" selected>.{{ s.ext }}</option>
              }
            </select>
          </label>
          <label class="docs-field">
            <span>{{ 'documents.filters.from' | translate }}</span>
            <input type="date" class="p-inputtext doc-input" [value]="s.from ?? ''" (change)="setDay('from', $any($event.target).value)" />
          </label>
          <label class="docs-field">
            <span>{{ 'documents.filters.to' | translate }}</span>
            <input type="date" class="p-inputtext doc-input" [value]="s.to ?? ''" (change)="setDay('to', $any($event.target).value)" />
          </label>
          <label class="docs-field">
            <span>{{ 'documents.filters.pageSize' | translate }}</span>
            <select class="p-inputtext doc-input" (change)="setPageSize(num($any($event.target).value))">
              @for (n of pageSizes; track n) {
                <option [value]="n" [selected]="s.pageSize === n">{{ n }}</option>
              }
            </select>
          </label>

          <!-- tag conditions: all must match, or any of them -->
          <fieldset class="docs-tag-filters">
            <legend>{{ 'documents.tagSearch.title' | translate }}</legend>
            @if (tagRows().length > 1) {
              <label class="docs-field docs-tag-match">
                <span>{{ 'documents.tagSearch.match' | translate }}</span>
                <select class="p-inputtext doc-input" (change)="patch({ tagAny: $any($event.target).value === 'any' })">
                  <option value="all" [selected]="!s.tagAny">{{ 'documents.tagSearch.matchAll' | translate }}</option>
                  <option value="any" [selected]="s.tagAny">{{ 'documents.tagSearch.matchAny' | translate }}</option>
                </select>
              </label>
            }
            @for (t of tagRows(); track $index; let i = $index) {
              <div class="docs-tag-row">
                <!-- any tag name (typed or picked): the prefix is added by the server -->
                <input
                  class="p-inputtext doc-input docs-tag-key"
                  list="docs-tag-names"
                  maxlength="60"
                  [attr.aria-label]="'documents.tagSearch.tag' | translate"
                  [placeholder]="'documents.tagSearch.chooseTag' | translate"
                  [value]="t.key"
                  (change)="setTagRow(i, { key: $any($event.target).value.trim(), value: t.key === $any($event.target).value.trim() ? t.value : '' })" />
                <select class="p-inputtext doc-input docs-tag-op" [attr.aria-label]="'documents.tagSearch.operator' | translate" (change)="setTagRow(i, { op: $any($event.target).value })">
                  @for (o of tagOps; track o) {
                    <option [value]="o" [selected]="o === t.op">{{ 'documents.tagSearch.ops.' + o | translate }}</option>
                  }
                </select>
                @if (t.op !== 'set' && t.op !== 'unset') {
                  <input
                    class="p-inputtext doc-input docs-tag-value"
                    [attr.list]="'docs-tag-values-' + i"
                    [attr.aria-label]="'documents.tagSearch.value' | translate"
                    [placeholder]="'documents.tagSearch.value' | translate"
                    [disabled]="!t.key"
                    [value]="t.value"
                    (focus)="suggest(i, $any($event.target).value)"
                    (input)="onTagValue(i, $any($event.target).value)" />
                  <datalist [id]="'docs-tag-values-' + i">
                    @for (v of (tagSuggestions()[i] ?? []); track v.value) {
                      <option [value]="v.value">{{ v.value }} ({{ v.count }})</option>
                    }
                  </datalist>
                }
                <button type="button" class="doc-icon-btn" (click)="removeTagRow(i)" [attr.aria-label]="'documents.filters.remove' | translate" [title]="'documents.filters.remove' | translate">
                  <i class="fa-solid fa-xmark" aria-hidden="true"></i>
                </button>
              </div>
            }
            <button type="button" class="doc-link-btn" [disabled]="tagRows().length >= 10" (click)="addTagRow()">
              <i class="fa-solid fa-plus" aria-hidden="true"></i>{{ 'documents.tagSearch.add' | translate }}
            </button>
            <datalist id="docs-tag-names">
              @for (k of tagKeys(); track k.key) {
                <option [value]="k.key">{{ tagKeyLabel(k.key) }}</option>
              }
            </datalist>
          </fieldset>

          <!-- technical data (not tags) -->
          <fieldset class="docs-tag-filters docs-tech-filters">
            <legend>{{ 'documents.techFilters.title' | translate }}</legend>
            <div class="docs-tag-row">
              <label class="docs-field">
                <span>{{ 'documents.techFilters.sizeMin' | translate }}</span>
                <input type="number" min="0" step="0.1" class="p-inputtext doc-input docs-num" [value]="s.sizeMin ?? ''" (change)="setSize('sizeMin', $any($event.target).value)" />
              </label>
              <label class="docs-field">
                <span>{{ 'documents.techFilters.sizeMax' | translate }}</span>
                <input type="number" min="0" step="0.1" class="p-inputtext doc-input docs-num" [value]="s.sizeMax ?? ''" (change)="setSize('sizeMax', $any($event.target).value)" />
              </label>
              <label class="docs-field">
                <span>{{ 'documents.technical.UFA_OCR_STATUS' | translate }}</span>
                <select class="p-inputtext doc-input" (change)="patch({ ocr: $any($event.target).value || null })">
                  <option value="" [selected]="!s.ocr">{{ 'documents.filters.any' | translate }}</option>
                  @for (o of ocrStatuses; track o) {
                    <option [value]="o" [selected]="s.ocr === o">{{ 'documents.ocr.' + o | translate }}</option>
                  }
                </select>
              </label>
              <label class="docs-field">
                <span>{{ 'documents.techFilters.updatedFrom' | translate }}</span>
                <input type="date" class="p-inputtext doc-input" [value]="s.updatedFrom ?? ''" (change)="setUpdated('from', $any($event.target).value)" />
              </label>
              <label class="docs-field">
                <span>{{ 'documents.techFilters.updatedTo' | translate }}</span>
                <input type="date" class="p-inputtext doc-input" [value]="s.updatedTo ?? ''" (change)="setUpdated('to', $any($event.target).value)" />
              </label>
              <label class="docs-check docs-tech-check">
                <input type="checkbox" [checked]="s.versioned" (change)="patch({ versioned: $any($event.target).checked })" />
                {{ 'documents.techFilters.versioned' | translate }}
              </label>
            </div>
          </fieldset>
        </div>
      }

      @if (activeKeys().length) {
        <div class="docs-chips" role="list" [attr.aria-label]="'documents.filters.active' | translate">
          @for (k of activeKeys(); track k) {
            <span class="docs-chip" role="listitem">
              @switch (k) {
                @case ('type') {
                  {{ 'documents.columns.type' | translate }}: {{ typeLabel(s.type) }}
                }
                @case ('table') {
                  {{ 'documents.columns.table' | translate }}: {{ tableLabel() }}
                }
                @case ('record') {
                  {{ 'documents.columns.record' | translate }}: {{ recordLabel() }}
                }
                @case ('q') {
                  {{ (s.nameOnly ? 'documents.search.nameOnly' : 'documents.search.label') | translate }}: "{{ s.q }}"
                }
                @case ('ext') {
                  .{{ s.ext }}
                }
                @case ('period') {
                  {{ s.from ? fmt().date(s.from + 'T00:00:00') : '...' }} - {{ s.to ? fmt().date(s.to + 'T00:00:00') : '...' }}
                }
                @case ('size') {
                  {{ 'documents.technical.UFA_FILE_SIZE' | translate }}: {{ s.sizeMin ?? 0 }} - {{ s.sizeMax ?? '...' }} MB
                }
                @case ('ocr') {
                  {{ 'documents.technical.UFA_OCR_STATUS' | translate }}: {{ 'documents.ocr.' + s.ocr | translate }}
                }
                @case ('versioned') {
                  {{ 'documents.techFilters.versioned' | translate }}
                }
                @case ('updated') {
                  {{ 'documents.technical.UFA_UPDATED_DATE' | translate }}: {{ s.updatedFrom ? fmt().date(s.updatedFrom + 'T00:00:00') : '...' }} - {{ s.updatedTo ? fmt().date(s.updatedTo + 'T00:00:00') : '...' }}
                }
                @default {
                  {{ tagChip(k) }}
                }
              }
              <button type="button" class="docs-chip-x" (click)="removeFilter(k)" [attr.aria-label]="'documents.filters.remove' | translate">
                <i class="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </span>
          }
          <button type="button" class="doc-link-btn" (click)="clearFilters()">{{ 'documents.filters.clear' | translate }}</button>
        </div>
      }

      @if (page()?.nameMatch) {
        <p class="doc-note"><i class="fa-solid fa-circle-info" aria-hidden="true"></i>{{ 'documents.search.nameMatch' | translate: { q: s.q } }}</p>
      }

      <!-- bulk actions -->
      @if (selection().size) {
        <div class="docs-bulk" role="toolbar" [attr.aria-label]="'documents.bulk.label' | translate">
          <span class="docs-bulk-count">{{ 'documents.bulk.selected' | translate: { count: selection().size } }}</span>
          @if (inBin()) {
            <button type="button" class="p-button p-button-secondary docs-tool-btn" [disabled]="busy()" (click)="restoreItems(selectedItems())">
              <i class="fa-solid fa-rotate-left" aria-hidden="true"></i>{{ 'documents.actions.restore' | translate }}
            </button>
            <button type="button" class="p-button p-button-alert docs-tool-btn" [disabled]="busy()" (click)="purgeItems(selectedItems())">
              <i class="fa-solid fa-trash" aria-hidden="true"></i>{{ (confirmPurge() ? 'documents.actions.purgeConfirm' : 'documents.actions.purge') | translate }}
            </button>
          } @else {
            <button type="button" class="p-button p-button-secondary docs-tool-btn" [disabled]="busy()" (click)="downloadSelection()">
              <i class="fa-solid" [class]="busy() ? 'fa-circle-notch fa-spin' : 'fa-file-zipper'" aria-hidden="true"></i>{{ (selection().size > 1 ? 'documents.actions.downloadZip' : 'documents.actions.download') | translate }}
            </button>
            @if (canWrite()) {
              <button type="button" class="p-button p-button-secondary docs-tool-btn" [disabled]="busy()" (click)="openAction('tags', selectedItems())">
                <i class="fa-solid fa-tags" aria-hidden="true"></i>{{ 'documents.bulk.tagsTitle' | translate }}
              </button>
              <button type="button" class="p-button p-button-secondary docs-tool-btn" [disabled]="busy()" (click)="openAction('link', selectedItems())">
                <i class="fa-solid fa-link" aria-hidden="true"></i>{{ 'documents.link.title' | translate }}
              </button>
              <button type="button" class="p-button p-button-secondary docs-tool-btn" [disabled]="busy()" (click)="trashItems(selectedItems())">
                <i class="fa-solid fa-trash-can" aria-hidden="true"></i>{{ 'documents.actions.trash' | translate }}
              </button>
            }
          }
          <button type="button" class="doc-link-btn" (click)="clearSelection()">{{ 'documents.bulk.clear' | translate }}</button>
        </div>
      }

      <div class="docs-list-wrap" tabindex="-1" (keydown)="onListKey($event)">
        @if (listState() === 'loading' && !page()) {
          <div class="docs-skeleton" aria-busy="true">
            @for (i of [1, 2, 3, 4, 5, 6]; track i) {
              <div class="docs-skeleton-row"></div>
            }
            <span class="visually-hidden">{{ 'documents.state.loading' | translate }}</span>
          </div>
        } @else if (listState() === 'error') {
          <p class="doc-state doc-state--error">
            <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ listError() }}
            <button type="button" class="doc-link-btn" (click)="reload()">{{ 'documents.actions.retry' | translate }}</button>
          </p>
        } @else if (!items().length) {
          <div class="docs-empty">
            <i class="fa-solid" [class]="inBin() ? 'fa-trash-can' : activeKeys().length ? 'fa-magnifying-glass' : 'fa-folder-open'" aria-hidden="true"></i>
            <p>
              {{ (inBin() ? 'documents.empty.bin' : activeKeys().length ? 'documents.empty.filtered' : s.folder === 'all' ? 'documents.empty.all' : 'documents.empty.folder') | translate }}
            </p>
            @if (activeKeys().length) {
              <button type="button" class="p-button p-button-secondary docs-tool-btn" (click)="clearFilters()">{{ 'documents.filters.clear' | translate }}</button>
            } @else if (canWrite() && !inBin()) {
              <button type="button" class="p-button p-button-primary docs-tool-btn" (click)="pickFiles()">
                <i class="fa-solid fa-cloud-arrow-up" aria-hidden="true"></i>{{ 'documents.empty.upload' | translate }}
              </button>
              <p class="doc-muted">{{ 'documents.empty.drop' | translate }}</p>
            }
          </div>
        } @else if (s.view === 'list') {
          <table class="docs-table" [class.docs-table--loading]="listState() === 'loading'" [attr.aria-busy]="listState() === 'loading'">
            <thead>
              <tr>
                <th class="docs-col-check" scope="col">
                  <input type="checkbox" [checked]="allSelected()" [indeterminate]="selection().size > 0 && !allSelected()" (change)="toggleAll()" [attr.aria-label]="'documents.bulk.selectAll' | translate" />
                </th>
                <th scope="col" [attr.aria-sort]="ariaSort('name')">
                  <button type="button" class="docs-th-btn" (click)="setSort('name')">
                    {{ 'documents.columns.name' | translate }}
                    @if (sortDir('name'); as dir) {<i class="fa-solid" [class]="dir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'" aria-hidden="true"></i>}
                  </button>
                </th>
                <th scope="col" class="docs-col-type" [attr.aria-sort]="ariaSort('type')">
                  <button type="button" class="docs-th-btn" (click)="setSort('type')">
                    {{ 'documents.columns.type' | translate }}
                    @if (sortDir('type'); as dir) {<i class="fa-solid" [class]="dir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'" aria-hidden="true"></i>}
                  </button>
                </th>
                <th scope="col" class="docs-col-record">{{ 'documents.columns.record' | translate }}</th>
                <th scope="col" class="docs-col-size" [attr.aria-sort]="ariaSort('size')">
                  <button type="button" class="docs-th-btn" (click)="setSort('size')">
                    {{ 'documents.columns.size' | translate }}
                    @if (sortDir('size'); as dir) {<i class="fa-solid" [class]="dir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'" aria-hidden="true"></i>}
                  </button>
                </th>
                <th scope="col" class="docs-col-date" [attr.aria-sort]="ariaSort('date')">
                  <button type="button" class="docs-th-btn" (click)="setSort('date')">
                    {{ (inBin() ? 'documents.columns.binnedOn' : 'documents.columns.created') | translate }}
                    @if (sortDir('date'); as dir) {<i class="fa-solid" [class]="dir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'" aria-hidden="true"></i>}
                  </button>
                </th>
                <th scope="col" class="docs-col-user">{{ 'documents.columns.uploadedBy' | translate }}</th>
                <th scope="col" class="docs-col-actions"><span class="visually-hidden">{{ 'documents.columns.actions' | translate }}</span></th>
              </tr>
            </thead>
            <tbody>
              @for (it of items(); track it.docId; let i = $index) {
                <tr
                  class="docs-row"
                  [class.docs-row--selected]="isSelected(it)"
                  [class.docs-row--open]="s.doc === it.docId"
                  [attr.data-doc-index]="i"
                  [attr.tabindex]="cursor() === i || (cursor() < 0 && i === 0) ? 0 : -1"
                  [attr.aria-selected]="isSelected(it)"
                  (click)="onRowClick(it, i, $event)"
                  (dblclick)="onRowDblClick(it)"
                  (focus)="cursor.set(i)">
                  <td class="docs-col-check">
                    <input type="checkbox" [checked]="isSelected(it)" (click)="$event.stopPropagation(); toggleSelect(it, i, $event)" [attr.aria-label]="'documents.bulk.select' | translate: { name: it.fileName }" />
                  </td>
                  <td class="docs-col-name">
                    <span class="docs-name">
                      <i class="docs-file-icon" [class]="icon(it.fileName)" aria-hidden="true"></i>
                      <span class="docs-name-text" [title]="it.fileName">{{ it.fileName }}</span>
                      @if (it.link) {<i class="fa-solid fa-link doc-muted" [title]="'documents.detail.isLink' | translate" aria-hidden="true"></i>}
                      @if (it.archived) {<i class="fa-solid fa-lock doc-muted" [title]="'documents.detail.archived' | translate" aria-hidden="true"></i>}
                    </span>
                    @if (it.tags.length) {
                      <span class="docs-row-tags">
                        @for (t of it.tags; track t.key) {
                          <button type="button" class="docs-tag" (click)="filterByTag(t.key, t.value ?? '')" [title]="'documents.filters.byTag' | translate">{{ t.value }}</button>
                        }
                      </span>
                    }
                  </td>
                  <td class="docs-col-type">{{ typeLabel(it.type) }}</td>
                  <td class="docs-col-record">
                    @if (it.tableId) {
                      <button type="button" class="doc-link-btn docs-record-btn" (click)="openRecordFolder(it)" [disabled]="!it.recordId" [title]="'documents.actions.recordDocuments' | translate">
                        {{ it.recordLabel || (it.recordId ? '#' + it.recordId : '') }}
                        <span class="doc-muted">{{ it.tableLabel }}</span>
                      </button>
                    }
                  </td>
                  <td class="docs-col-size">{{ size(it.size) }}</td>
                  <td class="docs-col-date" [title]="fmt().exact(inBin() ? it.trashedAt : it.created)">{{ fmt().dateTime(inBin() ? it.trashedAt : it.created) }}</td>
                  <td class="docs-col-user">{{ it.uploadedBy }}</td>
                  <td class="docs-col-actions">
                    <button type="button" class="doc-icon-btn" [class.doc-fav--on]="it.favourite" [attr.aria-pressed]="it.favourite" (click)="toggleFavourite(it, !it.favourite)" [attr.aria-label]="(it.favourite ? 'documents.actions.unfavourite' : 'documents.actions.favourite') | translate" [title]="(it.favourite ? 'documents.actions.unfavourite' : 'documents.actions.favourite') | translate">
                      <i class="fa-star" [class.fa-solid]="it.favourite" [class.fa-regular]="!it.favourite" aria-hidden="true"></i>
                    </button>
                    <button type="button" class="doc-icon-btn" (click)="download(it)" [attr.aria-label]="'documents.actions.download' | translate" [title]="'documents.actions.download' | translate">
                      <i class="fa-solid" [class]="it.link ? 'fa-arrow-up-right-from-square' : 'fa-download'" aria-hidden="true"></i>
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <ul class="docs-tiles" [class.docs-table--loading]="listState() === 'loading'" role="listbox" aria-multiselectable="true" [attr.aria-label]="'documents.list.label' | translate">
            @for (it of items(); track it.docId; let i = $index) {
              <li
                class="docs-tile"
                role="option"
                [class.docs-row--selected]="isSelected(it)"
                [class.docs-row--open]="s.doc === it.docId"
                [attr.data-doc-index]="i"
                [attr.tabindex]="cursor() === i || (cursor() < 0 && i === 0) ? 0 : -1"
                [attr.aria-selected]="isSelected(it)"
                (click)="onRowClick(it, i, $event)"
                (dblclick)="onRowDblClick(it)"
                (focus)="cursor.set(i)">
                <input type="checkbox" class="docs-tile-check" [checked]="isSelected(it)" (click)="$event.stopPropagation(); toggleSelect(it, i, $event)" [attr.aria-label]="'documents.bulk.select' | translate: { name: it.fileName }" />
                <button type="button" class="doc-icon-btn docs-tile-fav" [class.doc-fav--on]="it.favourite" [attr.aria-pressed]="it.favourite" (click)="toggleFavourite(it, !it.favourite)" [attr.aria-label]="(it.favourite ? 'documents.actions.unfavourite' : 'documents.actions.favourite') | translate">
                  <i class="fa-star" [class.fa-solid]="it.favourite" [class.fa-regular]="!it.favourite" aria-hidden="true"></i>
                </button>
                <iu-document-thumb [docId]="it.docId" [fileName]="it.fileName" [size]="it.size" [version]="it.version" />
                <span class="docs-tile-name" [title]="it.fileName">{{ it.fileName }}</span>
                <span class="docs-tile-meta">
                  {{ size(it.size) }}@if (it.size !== null) { - }{{ fmt().date(inBin() ? it.trashedAt : it.created) }}
                </span>
                @if (it.tableId) {
                  <span class="docs-tile-meta docs-tile-record">{{ it.recordLabel || it.tableLabel }}</span>
                }
              </li>
            }
          </ul>
        }
      </div>

      @if (pageInfo(); as pi) {
        <div class="docs-pager">
          <span class="doc-muted">{{ 'documents.list.range' | translate: { from: fmt().number(pi.from), to: fmt().number(pi.to), total: fmt().number(pi.total) } }}</span>
          <span class="docs-spacer"></span>
          <button type="button" class="p-button p-button-secondary docs-tool-btn docs-tool-btn--icon" [disabled]="s.page === 0" (click)="setPage(s.page - 1)" [attr.aria-label]="'documents.list.previous' | translate">
            <i class="fa-solid fa-chevron-left" aria-hidden="true"></i>
          </button>
          <button type="button" class="p-button p-button-secondary docs-tool-btn docs-tool-btn--icon" [disabled]="!hasNext()" (click)="setPage(s.page + 1)" [attr.aria-label]="'documents.list.next' | translate">
            <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      }

      @if (dragging()) {
        <div class="docs-drop" aria-hidden="true">
          <i class="fa-solid fa-cloud-arrow-up"></i>
          <p>
            @if (uploadTarget()) {
              {{ 'documents.upload.dropToRecord' | translate: { record: recordLabel() } }}
            } @else {
              {{ 'documents.upload.drop' | translate }}
            }
          </p>
        </div>
      }
    </section>

    <!-- detail -->
    @if (detailOpen()) {
      <aside class="docs-detail doc-detail" [attr.aria-label]="'documents.detail.label' | translate" (keydown.escape)="closeDetail()">
        @let sel = selectedItem();
        <iu-document-detail
          [row]="sel"
          [detail]="detail()"
          [loading]="detailLoading()"
          [error]="detailError()"
          [fmt]="fmt()"
          [locale]="locale"
          [busy]="busy()"
          [canRetryOcr]="canWrite()"
          [tagNames]="tagKeys()"
          (filterTag)="filterByTag($event.key, $event.value)"
          [versionProgress]="versionProgress()"
          [q]="s.nameOnly ? '' : s.q"
          (closed)="closeDetail()"
          (retry)="retryDetail()"
          (rename)="sel && rename(sel, $event)"
          (saveTags)="sel && saveTags(sel, $event)"
          (favourite)="sel && toggleFavourite(sel, $event)"
          (download)="sel && download(sel)"
          (openTab)="sel && openInTab(sel)"
          (copyLink)="sel && copyLink(sel)"
          (trash)="sel && trashItems([sel])"
          (restore)="sel && restoreItems([sel])"
          (purge)="sel && purgeItems([sel], true)"
          (openRecord)="openRecord($event)"
          (showRecord)="openRecordFolder($event)"
          (linkRecord)="openAction('link', [$event])"
          (retryOcr)="sel && retryOcr(sel)"
          (uploadVersion)="sel && uploadVersion(sel, $event)"
          (restoreVersion)="sel && restoreVersion(sel, $event)"
          (downloadVersion)="sel && downloadVersion(sel, $event)" />
      </aside>
    }
  </div>

  <!-- upload queue -->
  @if (uploads().length) {
    @let us = uploadSummary();
    <section class="docs-uploads" [attr.aria-label]="'documents.upload.queue' | translate">
      <header class="docs-uploads-head">
        <button type="button" class="docs-uploads-toggle" [attr.aria-expanded]="uploadsOpen()" (click)="uploadsOpen.set(!uploadsOpen())">
          @if (us.active) {
            <i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>{{ 'documents.upload.running' | translate: { done: us.done, total: us.total } }}
          } @else {
            <i class="fa-solid" [class]="us.failed ? 'fa-triangle-exclamation' : 'fa-circle-check'" aria-hidden="true"></i>{{ 'documents.upload.finished' | translate: { done: us.done, failed: us.failed } }}
          }
        </button>
        @if (!us.active) {
          <button type="button" class="doc-icon-btn" (click)="clearUploads()" [attr.aria-label]="'documents.upload.clear' | translate" [title]="'documents.upload.clear' | translate">
            <i class="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        }
      </header>
      @if (uploadsOpen()) {
        <ul class="docs-uploads-list">
          @for (u of uploads(); track u.id) {
            <li class="docs-upload" [class]="'docs-upload--' + u.state">
              <i class="docs-file-icon" [class]="icon(u.file.name)" aria-hidden="true"></i>
              <span class="docs-upload-main">
                @if (u.state === 'done' && u.docId) {
                  <button type="button" class="doc-link-btn docs-upload-name" (click)="openUploaded(u)">{{ u.file.name }}</button>
                } @else {
                  <span class="docs-upload-name" [title]="u.file.name">{{ u.file.name }}</span>
                }
                @if (u.state === 'uploading' || u.state === 'waiting') {
                  <span class="docs-progress" role="progressbar" [attr.aria-valuenow]="u.progress" aria-valuemin="0" aria-valuemax="100" [attr.aria-label]="u.file.name">
                    <span class="docs-progress-bar" [style.width.%]="u.progress"></span>
                  </span>
                } @else if (u.error) {
                  <span class="docs-upload-error">{{ u.error }}</span>
                } @else {
                  <span class="doc-muted">{{ size(u.file.size) }} - {{ 'documents.upload.state.' + u.state | translate }}</span>
                }
              </span>
              @if (u.state === 'uploading' || u.state === 'waiting') {
                <button type="button" class="doc-icon-btn" (click)="cancelUpload(u)" [attr.aria-label]="'documents.upload.cancel' | translate" [title]="'documents.upload.cancel' | translate">
                  <i class="fa-solid fa-ban" aria-hidden="true"></i>
                </button>
              } @else if (u.state === 'error' || u.state === 'cancelled') {
                <button type="button" class="doc-icon-btn" (click)="retryUpload(u)" [attr.aria-label]="'documents.upload.retry' | translate" [title]="'documents.upload.retry' | translate">
                  <i class="fa-solid fa-rotate-right" aria-hidden="true"></i>
                </button>
              }
            </li>
          }
        </ul>
      }
    </section>
  }

  <!-- status bar -->
  <div class="docs-notice-zone" aria-live="polite">
    @if (notice(); as n) {
      <div class="docs-notice" [class]="'docs-notice--' + n.kind">
        <span>{{ n.text }}</span>
        @if (n.undo) {
          <button type="button" class="docs-notice-btn" (click)="runUndo(n)">{{ 'documents.actions.undo' | translate }}</button>
        }
        <button type="button" class="doc-icon-btn" (click)="dismissNotice()" [attr.aria-label]="'documents.actions.close' | translate">
          <i class="fa-solid fa-xmark" aria-hidden="true"></i>
        </button>
      </div>
    }
  </div>

  <input #fileInput type="file" multiple hidden (change)="onFilesPicked(fileInput)" />

  <!-- dialogs -->
  @if (pendingFiles(); as files) {
    <iu-document-upload-dialog
      [files]="files"
      [initialTarget]="uploadTarget()"
      [tagDefs]="tagDefs()"
      [maxUploadBytes]="f?.maxUploadBytes ?? 0"
      [initialAllRoles]="allRoles()"
      (confirmed)="startUploads($event.files, $event.options)"
      (cancelled)="pendingFiles.set(null)" />
  }
  @if (action(); as a) {
    <iu-document-action-dialog
      [kind]="a.kind"
      [count]="a.items.length"
      [tagDefs]="tagDefs()"
      [initialTarget]="a.target"
      [canUnlink]="a.canUnlink"
      (confirmed)="runAction($event)"
      (cancelled)="action.set(null)" />
  }
  @if (tagManagerOpen()) {
    <iu-document-tag-manager [tagNames]="tagKeys()" (renamed)="onTagRenamed($event)" (closed)="tagManagerOpen.set(false)" />
  }
</div>
